import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="toast"
export default class extends Controller {
  static targets = ["container"];

  connect() {
    // Make controller available globally for programmatic toasts
    window.ToastController = this;
  }

  /**
   * Show a flash notification
   * @param {string} message - The message to display
   * @param {string} type - The type of flash: 'success', 'error', 'warning', 'info'
   * @param {number} duration - Duration in milliseconds (default: 5000)
   */
  show(message, type = 'success', duration = 5000) {
    const flash = this.createFlash(message, type);
    this.containerTarget.appendChild(flash);

    // Auto-remove after duration
    setTimeout(() => {
      this.removeFlash(flash);
    }, duration);
  }

  success(message, duration) {
    this.show(message, 'success', duration);
  }

  error(message, duration) {
    this.show(message, 'error', duration);
  }

  warning(message, duration) {
    this.show(message, 'warning', duration);
  }

  info(message, duration) {
    this.show(message, 'info', duration);
  }

  createFlash(message, type) {
    const typeMap = {
      success: 'notice',
      error: 'alert',
      warning: 'warning',
      info: 'info'
    };
    const flashType = typeMap[type] || 'notice';

    const flash = document.createElement('div');
    flash.className = `flash flash--${flashType}`;
    flash.setAttribute('role', flashType === 'alert' ? 'alert' : 'status');
    flash.setAttribute('aria-live', flashType === 'alert' ? 'assertive' : 'polite');

    const icons = {
      success: '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>',
      error: '<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>',
      warning: '<path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>',
      info: '<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 5a1 1 0 01-1 1v2a1 1 0 102 0v-2a1 1 0 01-1-1z" clip-rule="evenodd"/>'
    };

    flash.innerHTML = `
      <span class="flash__icon">
        <svg viewBox="0 0 20 20" fill="currentColor">
          ${icons[type] || icons.success}
        </svg>
      </span>
      <span class="flash__message">${message}</span>
    `;

    return flash;
  }

  removeFlash(flash) {
    flash.classList.add('flash--hiding');
    flash.addEventListener('animationend', () => {
      flash.remove();
    }, { once: true });
  }
}
